import * as THREE from "three";
import { VRButton } from "three/examples/jsm/webxr/VRButton.js";

export interface XRRig {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /**
   * The locomotion root. Move/rotate this (not the camera) to reposition the
   * player. The camera is parented here so headset tracking composes with it.
   */
  player: THREE.Group;
  /** Desktop / session-end spawn pose for the player rig. */
  spawn: { position: THREE.Vector3; yaw: number };
  /** Restore player rig to spawn (does not touch headset-tracked camera). */
  resetPlayerPose: () => void;
}

// Center-aisle spawn facing the primary work wall; roomy enough to walk toward
// additional stations once environment widens the shell (see world/room.ts).
const SPAWN_POSITION = new THREE.Vector3(0, 0, 2.2);
const SPAWN_YAW = 0;
/** Comfortable standing eye height used for desktop fallback only. */
const DESKTOP_EYE_HEIGHT = 1.6;

/**
 * Create the renderer, scene, camera hierarchy and player rig. This is the one
 * obvious place that owns XR session configuration; other systems build on the
 * returned rig. Desktop fallback shares the same scene and update loop.
 *
 * Reference space: `local-floor` (y=0 at physical floor) for room-scale standing
 * on Quest. Seated experiences would switch to `local` instead.
 */
export function createXRRig(container: HTMLElement): XRRig {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  // PCF (not Soft) — Soft is a heavy stereo fill cost on Quest-class GPUs.
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.xr.enabled = true;
  // local-floor puts y=0 at the physical floor for room-scale standing use.
  renderer.xr.setReferenceSpaceType("local-floor");
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b0e13);
  scene.fog = new THREE.Fog(0x0b0e13, 6, 22);

  const camera = new THREE.PerspectiveCamera(
    70,
    window.innerWidth / window.innerHeight,
    0.05,
    100,
  );

  // Player rig: camera is a child so headset pose is added on top of the rig
  // transform. Desktop starts with a sensible standing eye height.
  const player = new THREE.Group();
  player.name = "player-rig";
  camera.position.set(0, DESKTOP_EYE_HEIGHT, 0);
  player.add(camera);
  player.position.copy(SPAWN_POSITION);
  player.rotation.y = SPAWN_YAW;
  scene.add(player);

  const spawn = {
    position: SPAWN_POSITION.clone(),
    yaw: SPAWN_YAW,
  };

  const resetPlayerPose = (): void => {
    player.position.copy(spawn.position);
    player.rotation.set(0, spawn.yaw, 0);
    // Restore desktop eye height; in XR the headset overrides camera pose.
    if (!renderer.xr.isPresenting) {
      camera.position.set(0, DESKTOP_EYE_HEIGHT, 0);
      camera.rotation.set(0, 0, 0);
    }
  };

  document.body.appendChild(VRButton.createButton(renderer));

  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  return { renderer, scene, camera, player, spawn, resetPlayerPose };
}
